/**
 * Everything a renderer needs to draw one picture of the tiling. Shared by the WebGL renderer
 * and the CPU fallback so both draw exactly the same thing.
 */
import {
  type RotationColouring,
  identityPerm,
  packPerm,
  pairPermutations,
} from '../geometry/colouring';
import type { Isometry } from '../geometry/mobius';
import { MODEL_INDEX, type ModelId } from '../geometry/models';
import type { TriangleGroup } from '../geometry/triangle-group';
import type { Box } from '../paint/domain';
import type { ColourScheme, GridMode } from '../paint/codec';
import type { Camera } from './camera';
import { CLASS_COLOURS, COLORS, PAINT, type RGB, hexToRgb } from './palette';

export interface ScenePalette {
  ground: RGB;
  paint: [RGB, RGB, RGB, RGB];
  classGround: RGB[];
  classInk: RGB[];
  gold: RGB;
  line: RGB;
}

export const DEFAULT_PALETTE: ScenePalette = {
  ground: hexToRgb(COLORS.parchment),
  paint: PAINT.map((p) => hexToRgb(p.hex)) as [RGB, RGB, RGB, RGB],
  classGround: CLASS_COLOURS.map((c) => hexToRgb(c.ground)),
  classInk: CLASS_COLOURS.map((c) => hexToRgb(c.ink)),
  gold: hexToRgb(COLORS.gold),
  line: hexToRgb(COLORS.line),
};

/** Settings that change rarely (group, symmetry, colouring, motif). */
export interface SceneSettings {
  tg: TriangleGroup;
  rotation: boolean;
  scheme: ColourScheme;
  colouring: RotationColouring | null;
  grid: GridMode;
  /** Paint layer box in disk coordinates. */
  box: Box;
  /** Mean role coverages over the domain: [ink, vermilion, gold, ivory, total]. */
  coverage: [number, number, number, number, number];
  palette: ScenePalette;
}

/** Per-frame parameters. */
export interface FrameParams {
  camera: Camera;
  /** Device pixels per CSS pixel for this render. */
  pixelRatio: number;
  model: ModelId;
  morph: number;
  /** Inverse of the view isometry (orientation preserving). */
  viewInverse: Isometry;
  /** ρ(anchor)⁻¹ for rotation classes (identity otherwise). */
  baseInverse: number[];
  tileOutline: number;
  domainOutline: number;
  focus: number;
  /** Colour outside the plane, straight alpha; alpha 0 = transparent. */
  background: [number, number, number, number];
}

export const modelIndex = (m: ModelId): number => MODEL_INDEX[m];

/** Packed ρ(m_a m_b) table for the shader (identity when there is no colouring). */
export function packedPairs(c: RotationColouring | null): Uint32Array {
  const out = new Uint32Array(9);
  const pairs = c ? pairPermutations(c) : null;
  for (let i = 0; i < 9; i++) out[i] = packPerm(pairs ? pairs[i] : identityPerm(8));
  return out;
}

/** Average colour of the pattern, used where tiles are smaller than a pixel. */
export function averageColour(s: SceneSettings): RGB {
  const [ci, cv, cg, cy, total] = s.coverage;
  const pal = s.palette;
  let grounds: RGB[] = [pal.ground];
  let inks: RGB[] = [pal.paint[0]];
  if (s.scheme === 'parity' && !s.rotation) {
    grounds = [pal.ground, pal.paint[0]];
    inks = [pal.paint[0], pal.ground];
  } else if (s.scheme === 'classes' && s.colouring) {
    grounds = pal.classGround.slice(0, s.colouring.k);
    inks = pal.classInk.slice(0, s.colouring.k);
  }
  const mean = (list: RGB[]): RGB => {
    const out: RGB = [0, 0, 0];
    for (const c of list) for (let i = 0; i < 3; i++) out[i] += c[i] / list.length;
    return out;
  };
  const g = mean(grounds);
  const k = mean(inks);
  const out: RGB = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    out[i] =
      g[i] * (1 - total) +
      k[i] * ci +
      pal.paint[1][i] * cv +
      pal.paint[2][i] * cg +
      pal.paint[3][i] * cy;
  }
  return out;
}

/** A size scale for the triangle used to fade out sub-pixel detail. */
export const triangleSize = (tg: TriangleGroup): number => 0.6 * Math.sqrt(tg.area);
